// src/ArtistDetail.js
import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';

export default function ArtistDetail() {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [topTracks, setTopTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loadingArtist, setLoadingArtist] = useState(true);
  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingAlbums, setLoadingAlbums] = useState(true);
  const [showAllTop, setShowAllTop] = useState(false);
  const [error, setError] = useState('');

  // --- Albums pager (snap) state like HomePage ---
  const albumsScrollerRef = useRef(null);
  const rafScrollId = useRef(0);
  const [albumSnapX, setAlbumSnapX] = useState([]);
  const [albumCanPrev, setAlbumCanPrev] = useState(false);
  const [albumCanNext, setAlbumCanNext] = useState(false);

  const VISIBLE_ALBUMS = 6; // 👈 exactly 6 album cards shown at once

  // Helpers for album meta
  const getYear = (dateStr) => (dateStr ? String(dateStr).slice(0, 4) : '');

  const getAlbumType = (a) => {
    const t = (a.album_group || a.album_type || '').toLowerCase();
    const tracks = a.total_tracks || 0;
    if (t === 'compilation') return 'Compilation';
    if (t === 'appears_on') return 'Appears on';
    // Heuristic: Spotify often flags EPs as "single" with multiple tracks, or name contains "EP"
    if (t === 'single') return tracks > 1 ? 'EP' : 'Single';
    if (t === 'album') {
      if (/\bEP\b/i.test(a.name) || (tracks >= 3 && tracks <= 6)) return 'EP';
      return 'Album';
    }
    return 'Album';
  };
  
  // Filter state
  const [albumFilter, setAlbumFilter] = useState('all'); // 'all' | 'albums' | 'singles' | 'eps' | 'appears_on' | 'compilations'

  // Compute filtered list (recomputes when albums or filter changes)
  const displayedAlbums = useMemo(() => {
    switch (albumFilter) {
      case 'albums':        return albums.filter(a => getAlbumType(a) === 'Album');
      case 'singles':       return albums.filter(a => getAlbumType(a) === 'Single');
      case 'eps':           return albums.filter(a => getAlbumType(a) === 'EP');
      case 'appears_on':    return albums.filter(a => getAlbumType(a) === 'Appears on');
      case 'compilations':  return albums.filter(a => getAlbumType(a) === 'Compilation');
      default:              return albums; // 'all'
    }
  }, [albums, albumFilter]);

  const showPager = displayedAlbums.length > VISIBLE_ALBUMS;

  const onAlbumsScroll = () => {
    if (rafScrollId.current) return;
    rafScrollId.current = requestAnimationFrame(() => {
      updateAlbumArrows();
      rafScrollId.current = 0;
    });
  };

  const getNearestIndex = (positions, curr) => {
    if (!positions || positions.length === 0) return 0;
    let best = 0;
    let bestDist = Math.abs(positions[0] - curr);
    for (let i = 1; i < positions.length; i++) {
      const d = Math.abs(positions[i] - curr);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  };

  const getAlbumPageIdx = () => {
    const el = albumsScrollerRef.current;
    if (!el || albumSnapX.length === 0) return 0;
    return getNearestIndex(albumSnapX, el.scrollLeft);
  };

  const computeAlbumSnapPoints = () => {
    const el = albumsScrollerRef.current;
    if (!el) return;
    // SNAP **PER ALBUM**
    const tiles = Array.from(el.querySelectorAll('.album-snap'));
    if (tiles.length === 0) {
      setAlbumSnapX([]); setAlbumCanPrev(false); setAlbumCanNext(false);
      return;
    }
    const firstLeft = tiles[0].offsetLeft;
    const positions = tiles.map(t => t.offsetLeft - firstLeft);
    setAlbumSnapX(positions);

    const idx = getNearestIndex(positions, el.scrollLeft);
    const maxStart = Math.max(0, positions.length - VISIBLE_ALBUMS);
    setAlbumCanPrev(idx > 0);
    setAlbumCanNext(idx < maxStart);
  };

  const updateAlbumArrows = () => {
    const el = albumsScrollerRef.current;
    if (!el || albumSnapX.length === 0) { setAlbumCanPrev(false); setAlbumCanNext(false); return; }
    const idx = getNearestIndex(albumSnapX, el.scrollLeft);
    const maxStart = Math.max(0, albumSnapX.length - VISIBLE_ALBUMS);
    setAlbumCanPrev(idx > 0);
    setAlbumCanNext(idx < maxStart);
  };

  const scrollAlbumsToIndex = (idx) => {
    const el = albumsScrollerRef.current;
    if (!el || albumSnapX.length === 0) return;
    const maxStart = Math.max(0, albumSnapX.length - VISIBLE_ALBUMS);
    const clamped = Math.max(0, Math.min(idx, maxStart));
    el.scrollTo({ left: albumSnapX[clamped], behavior: 'smooth' });
  };

  const nextAlbumPage = () => scrollAlbumsToIndex(getAlbumPageIdx() + 1);
  const prevAlbumPage = () => scrollAlbumsToIndex(getAlbumPageIdx() - 1);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      // reset scroll to start whenever filter changes
      if (albumsScrollerRef.current) albumsScrollerRef.current.scrollLeft = 0;
      computeAlbumSnapPoints();
    });
    return () => cancelAnimationFrame(id);
  }, [displayedAlbums]);

  // also recompute on resize
  useEffect(() => {
    const onResize = () => computeAlbumSnapPoints();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const el = albumsScrollerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => computeAlbumSnapPoints());
    ro.observe(el);

    // also watch children size changes
    Array.from(el.children).forEach(ch => ro.observe(ch));

    return () => ro.disconnect();
  }, []);


  useEffect(() => {
    let cancelled = false;
    setShowAllTop(false);
    setError('');
    setLoadingArtist(true);
    setLoadingTop(true);
    setLoadingAlbums(true);

    // Artist details
    fetch(`/api/spotify/artists/${id}`)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load artist');
        return r.json();
      })
      .then((data) => { if (!cancelled) setArtist(data); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoadingArtist(false); });

    // Top tracks (US market) -> keep only top 10
    fetch(`/api/spotify/artists/${id}/top-tracks?market=US`)
      .then((r) => r.json())
      .then((data) => { if (!cancelled) setTopTracks((data.tracks || []).slice(0, 10)); })
      .catch(() => { if (!cancelled) setTopTracks([]); })
      .finally(() => { if (!cancelled) setLoadingTop(false); });

    // Albums / Singles
    fetch(`/api/spotify/artists/${id}/albums?include_groups=album,single,compilation,appears_on&market=US&limit=50`)
      .then((r) => r.json())
      .then((data) => { if (!cancelled) setAlbums(data.items || []); })
      .catch(() => { if (!cancelled) setAlbums([]); })
      .finally(() => { if (!cancelled) setLoadingAlbums(false); });

    return () => { cancelled = true; };
  }, [id]);

  const formatDuration = (ms) => {
    const mins = Math.floor(ms / 60000);
    const secs = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const TrackRow = ({ track, index }) => {
    // Prefer the smallest image if available, fallback to larger ones
    const thumb =
      track.album?.images?.[2]?.url ||
      track.album?.images?.[1]?.url ||
      track.album?.images?.[0]?.url;

    return (
      <Link
        to={`/track/${track.id}`}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <Card className="hover-shadow" style={{ border: 'none' }}>
          <Card.Body className="py-2">
            <Row className="align-items-center gx-3">
              <Col xs="auto" className="track-index-col text-end">{index + 1}</Col>
              <Col xs="auto" className="track-thumb-wrap">
                {thumb && (
                  <img
                    src={thumb}
                    alt={`${track.name} cover`}
                    className="track-thumb"
                  />
                )}
              </Col>
              <Col className="track-title">{track.name}</Col>
              <Col xs="auto" className="track-duration">
                {formatDuration(track.duration_ms)}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Link>
    );
  };

  // Decide how many top tracks to show (5 or all)
  const visibleTopTracks = showAllTop ? topTracks : topTracks.slice(0, 5);

  if (loadingArtist) {
    return (
      <Container className="my-4">
        <div className="text-center">
          <Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner>
          <p className="mt-2">Loading artist...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-4">
        <Alert variant="danger">
          <h4>Error loading artist</h4>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!artist) {
    return (
      <Container className="my-4">
        <Alert variant="warning">
          <h4>Artist not found</h4>
          <p>We couldn’t find that artist.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-4">
      {/* Header: image left, name + button right */}
      <Row className="mb-4">
        <Col xs={12}>
          <div className="song-detail-container">
            {artist.images?.[0]?.url && (
              <img
                src={artist.images[0].url}
                alt={artist.name}
                className="artist-image artist-image-left"
              />
            )}
            <div className="song-info">
              <h2 className="song-title">{artist.name}</h2>
              <Button
                href={artist.external_urls?.spotify}
                target="_blank"
                rel="noopener noreferrer"
                variant="success"
                className="mt-2"
              >
                <i className="bi bi-spotify"></i> View on Spotify
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {/* Top Tracks (stacked like album tracklist) */}
      <Row className="mb-4">
        <Col xs={12}>
          <h4 className="mb-3 text-start">Top Tracks</h4>
          {loadingTop ? (
            <div className="text-center my-3">
              <Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner>
            </div>
          ) : topTracks.length > 0 ? (
            <>
              <div className="tracklist">
                {visibleTopTracks.map((t, idx) => (
                  <TrackRow key={t.id} track={t} index={idx} />
                ))}
              </div>
              
              {/* Show less or See more button */}
              {topTracks.length > 5 && (
                <div className="mt-2 text-start">
                  <Button
                    className="btn-text-link"
                    variant="link"
                    size="sm"
                    onClick={() => setShowAllTop(s => !s)}
                  >
                    <span className="see-more-show-less fw-bold">
                      {showAllTop ? 'Show less' : 'See more'}
                    </span>
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted">No top tracks available.</p>
          )}
        </Col>
      </Row>

      {/* Albums & Singles (horizontal scroll, 6 visible, snap by 1) */}
      <Row className="mb-2">
        <Col xs={12}>
          <h4 className="mb-3 text-start">Discography</h4>
          <div className="d-flex gap-2 flex-wrap mb-3">
            <Button size="sm" variant={albumFilter==='all'?'secondary':'outline-secondary'} onClick={()=>setAlbumFilter('all')}>All</Button>
            <Button size="sm" variant={albumFilter==='albums'?'secondary':'outline-secondary'} onClick={()=>setAlbumFilter('albums')}>Albums</Button>
            <Button size="sm" variant={albumFilter==='singles'?'secondary':'outline-secondary'} onClick={()=>setAlbumFilter('singles')}>Singles</Button>
            <Button size="sm" variant={albumFilter==='eps'?'secondary':'outline-secondary'} onClick={()=>setAlbumFilter('eps')}>EPs</Button>
            <Button size="sm" variant={albumFilter==='appears_on'?'secondary':'outline-secondary'} onClick={()=>setAlbumFilter('appears_on')}>Appears On</Button>
            <Button size="sm" variant={albumFilter==='compilations'?'secondary':'outline-secondary'} onClick={()=>setAlbumFilter('compilations')}>Compilations</Button>
          </div>

          {loadingAlbums ? (
            <div className="text-center my-3">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : displayedAlbums.length > 0 ? (
            <div className="hscroll-wrap">
              <div className="album-fade-edges" style={{ '--album-fade-bg': '#fff', '--album-fade-size': '24px' }}>
                <div
                  className="albums-scroller hscroll"
                  ref={albumsScrollerRef}
                  onScroll={onAlbumsScroll}
                >
                  {displayedAlbums.map((album) => (
                    <div className="album-snap" key={album.id}>
                      <Card className="album-card">
                        {album.images?.[0]?.url && (
                          <Card.Img
                            src={album.images[0].url}
                            className="album-card-img"
                            loading="lazy"
                          />
                        )}
                        <Card.Body className="album-card-body">
                          <Card.Title className="album-card-title">
                            {album.name}
                          </Card.Title>
                          <div className="album-card-meta">
                            {getYear(album.release_date)} &middot; {getAlbumType(album)}
                          </div>
                        </Card.Body>
                        
                        {/* anchor that covers the whole card */}
                        <Link
                          to={`/album/${album.id}`}
                          className="album-card-stretched-link"
                          aria-label={`Open album: ${album.name}`}
                        />
                      </Card>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pager buttons */}
              <div
                style={{
                  display: 'flex',
                  gap: '0.25rem',
                  justifyContent: 'flex-end',
                  paddingRight: 'var(--album-fade-size, 24px)',
                  position: 'relative',
                  top: '0rem',
                  // 👇 reserve the same height always (tweak if you change button size)
                  minHeight: 31,
                  // 👇 invisible but still takes space when there are ≤6 items
                  visibility: showPager ? 'visible' : 'hidden',
                  pointerEvents: showPager ? 'auto' : 'none',
                }}
                aria-hidden={!showPager}
              >
                <button
                  type="button"
                  className="pager-btn"
                  onClick={prevAlbumPage}
                  disabled={!albumCanPrev}
                  aria-label="Previous albums"
                >
                  <svg width="13" height="13" viewBox="0 0 8 12" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="7,1 1,6 7,11" />
                  </svg>
                </button>
                <button
                  type="button"
                  className="pager-btn"
                  onClick={nextAlbumPage}
                  disabled={!albumCanNext}
                  aria-label="Next albums"
                >
                  <svg width="13" height="13" viewBox="0 0 8 12" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="1,1 7,6 1,11" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div
              className="albums-empty text-muted"
              style={{ '--albums-empty-height': '320px', '--album-fade-size': '24px' }} // ← tweak height here
            >
              No items for this filter.
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
}
